import { LogoutModal } from '@/features/authentication/_components/LogoutModal';
import { useMe } from '@/features/authentication/_hooks/me';
import { useLastCommentSearchHistory } from '@/features/comment/hooks/local-storage';
import { useFeatureFlags } from '@/features/feature-flag/_hooks/feature-flags';
import { useSearchHistory } from '@/features/project/_hooks/search-projects/local-storage';
import { SearchUsersModal } from '@/features/tenant-manager/components';
import { useLastWorkflowSearchHistory } from '@/features/workflow/hooks/local-storage';
import { ITEM_SPLITTER, isRS } from '@/utils';
import { URL_PATH } from '@/utils/route-path';
import {
  BarChartOutlined,
  LogoutOutlined,
  SearchOutlined,
  TableOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
} from '@ant-design/icons';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { ServiceLogo } from './ServiceLogo';
import { SidebarContent } from './SidebarContent';

const MENU_PATTERNS = {
  dashboard: /^dashboard(\?.*)?$/,
  analysis: /^analysis(\?.*)?$/,
  revenue: /^revenue(\?.*)?$/,
  project: /^project(\?.*)?$/,
  rsProject: /^rs\/project(\?.*)?$/,
  policy: /^policy(\?.*)?$/,
  fee: /^fee(\?.*)?$/,
  budget: /^budget(\?.*)?$/,
  indicator: /^indicator(\?.*)?$/,
  globalTag: /^tag\/global(\?.*)?$/,
  localTag: /^tag\/local(\?.*)?$/,
  masterTable: /^master-table(\?.*)?$/,
  tenantManager: /^tenant-manager(\?.*)?$/,
  workflow: /^workflow(\?.*)?$/,
  comment: /^comment(\?.*)?$/,
  cms: /^cms\/note(\?.*)?$/,
  dataImporter: /^data-importer(\?.*)?$/,
};

type Props = {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
};

export const PageSidebar: FC<Props> = ({ collapsed, setCollapsed }) => {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [
    openUsersOrOrganizationsSearchModal,
    setOpenUsersOrOrganizationsSearchModal,
  ] = useState(false);
  const { lastSearchedQueryParams } = useSearchHistory();
  const { commentPathWithQuery } = useLastCommentSearchHistory();
  const { workflowPathWithQuery } = useLastWorkflowSearchHistory();

  // const notificationBadges = useNotificationBadges();
  const { features } = useFeatureFlags();

  const getSelectedKey = (pathname: string) => {
    if (!pathname) {
      return '';
    }
    if (pathname.includes('rs/project')) {
      return `rs/project?${lastSearchedQueryParams.toString()}`;
    }
    if (pathname.includes('tag/global')) {
      return 'tag/global';
    }
    if (pathname.includes('tag/local')) {
      return 'tag/local';
    }
    if (pathname.includes(URL_PATH.comment)) {
      return commentPathWithQuery;
    }
    if (pathname.includes(URL_PATH.workflow)) {
      return workflowPathWithQuery;
    }
    return location.pathname.split('/')[1];
  };

  const selectedKey = getSelectedKey(location.pathname);

  const isNotJoinOrganization = !me.data?.organizationIds.length;

  const topMenu = useMemo(
    () =>
      [
        // {
        //   label: t('dashboard-menu'),
        //   key: 'dashboard',
        //   icon: <AppstoreOutlined aria-label="" />,
        // },
        // {
        //   label: t('analysis-menu'),
        //   key: 'analysis',
        //   icon: <ProjectOutlined aria-label="" />,
        // },
        // {
        //   label: t('policy-menu'),
        //   key: 'policy',
        //   icon: <ApartmentOutlined aria-label="" />,
        // },
        {
          label: t('project-menu'),
          key: `rs/project?${lastSearchedQueryParams.toString()}`,
          icon: <BarChartOutlined aria-label="" />,
        },
        // {
        //   label: t('revenue-menu'),
        //   key: 'revenue',
        //   icon: <MoneyCollectOutlined aria-label="" />,
        // },
        // {
        //   label: t('fee-page'),
        //   key: 'fee',
        //   icon: <FeeMenuIcon aria-label="" />,
        // },
        // {
        //   label: t('budget-menu'),
        //   key: 'budget',
        //   icon: <WalletOutlined aria-label="" />,
        // },
        // {
        //   label: t('indicator'),
        //   key: 'indicator',
        //   icon: <RiseOutlined aria-label="" />,
        // },
        // {
        //   key: commentPathWithQuery,
        //   label: t('comment'),
        //   icon: <CommentOutlined aria-label="" />,
        //   isShowNotificationBadge: notificationBadges.comment.isShow,
        //   notificationBadgeCount: notificationBadges.comment.count,
        //   notificationBadgeColor: isRS() ? 'red' : 'blue',
        // },
        // {
        //   key: workflowPathWithQuery,
        //   label: t('workflow'),
        //   icon: <WorkflowIcon aria-label="" />,
        //   isShowNotificationBadge: notificationBadges.workflow.isShow,
        //   notificationBadgeCount: notificationBadges.workflow.count,
        //   notificationBadgeColor: isRS() ? 'red' : 'blue',
        // },
        // {
        //   label: t('batch.menu'),
        //   key: 'batch',
        //   icon: <CodeOutlined aria-label="" />,
        // },
        // {
        //   label: t('scope-tag-menu', { scope: t('global-tag') }),
        //   key: 'tag/global',
        //   icon: <TagsOutlined aria-label="" />,
        // },
        // {
        //   label: t('scope-tag-menu', { scope: t('local-tag') }),
        //   key: 'tag/local',
        //   icon: <TagsOutlined aria-label="" />,
        // },
        // {
        //   label: t('cms'),
        //   key: 'cms/note',
        //   icon: <FormOutlined aria-label="" />,
        // },
        {
          label: t('master-table-menu'),
          key: 'master-table',
          icon: <TableOutlined aria-label="" />,
        },
        {
          label: t('tenant-manager-menu'),
          key: 'tenant-manager',
          icon: isRS() ? (
            <UsergroupAddOutlined aria-label="" />
          ) : (
            <TeamOutlined aria-label="" />
          ),
        },
        // {
        //   label: t('data-importer'),
        //   key: 'data-importer',
        //   icon: <FileSyncOutlined aria-label="" />,
        // },
      ]
        .filter((menu) => {
          if (isRS()) {
            return ![
              'dashboard',
              'revenue',
              'budget',
              'policy',
              'fee',
              'indicator',
              'cms/note',
              'data-importer',
            ].includes(menu.key);
          }
          return !['dashboard'].includes(menu.key);
        })
        .filter((menu) => {
          switch (true) {
            case MENU_PATTERNS.dashboard.test(menu.key):
            case MENU_PATTERNS.analysis.test(menu.key):
            case MENU_PATTERNS.revenue.test(menu.key):
            case MENU_PATTERNS.project.test(menu.key):
            case MENU_PATTERNS.rsProject.test(menu.key): {
              return (
                me.data?.role.resourceGroupPermissions.project === 'view' ||
                me.data?.role.resourceGroupPermissions.project === 'edit' ||
                me.data?.role.resourceGroupPermissions.project ===
                  'view-all-privilege' ||
                me.data?.role.resourceGroupPermissions.project === 'privilege'
              );
            }
            case MENU_PATTERNS.indicator.test(menu.key): {
              return (
                me.data?.role.resourceGroupPermissions.indicator === 'view' ||
                me.data?.role.resourceGroupPermissions.indicator === 'edit'
              );
            }
            case MENU_PATTERNS.globalTag.test(menu.key): {
              return (
                me.data?.role.resourceGroupPermissions.globalTag === 'view' ||
                me.data?.role.resourceGroupPermissions.globalTag === 'edit'
              );
            }
            case MENU_PATTERNS.localTag.test(menu.key): {
              return (
                me.data?.role.resourceGroupPermissions.localTag === 'view' ||
                me.data?.role.resourceGroupPermissions.localTag === 'edit'
              );
            }
            case MENU_PATTERNS.masterTable.test(menu.key): {
              return (
                me.data?.role.resourceGroupPermissions.masterTable === 'view' ||
                me.data?.role.resourceGroupPermissions.masterTable === 'edit'
              );
            }
            case MENU_PATTERNS.tenantManager.test(menu.key): {
              return (
                me.data?.role.resourceGroupPermissions.administrator ===
                  'view' ||
                me.data?.role.resourceGroupPermissions.administrator === 'edit'
              );
            }
            case MENU_PATTERNS.comment.test(menu.key):
              return features?.comment;
            case MENU_PATTERNS.workflow.test(menu.key):
              return features?.workflow;
            default:
              return !isNotJoinOrganization;
          }
        }),
    [
      features?.comment,
      features?.workflow,
      isNotJoinOrganization,
      me.data?.role.resourceGroupPermissions.administrator,
      me.data?.role.resourceGroupPermissions.globalTag,
      me.data?.role.resourceGroupPermissions.indicator,
      me.data?.role.resourceGroupPermissions.localTag,
      me.data?.role.resourceGroupPermissions.masterTable,
      me.data?.role.resourceGroupPermissions.project,
      // me.data?.role.resourceGroupPermissions.tenantManager,
      lastSearchedQueryParams,
      t,
    ],
  );

  const bottomMenu = useMemo(
    () =>
      [
        // {
        //   label: (
        //     <LinkText
        //       text={t('document-link')}
        //       color="normal"
        //       href="/docs/user-manual.pdf"
        //     />
        //   ),
        //   key: 'document-link',
        //   icon: <ReadOutlined aria-label="" />,
        //   hidden: !isRS(),
        // },
        // {
        //   label: (
        //     <LinkText
        //       text={t('contact-us-link')}
        //       color="normal"
        //       href="https://www.gyoukaku.go.jp/review/review.html"
        //     />
        //   ),
        //   key: 'contact-us-link',
        //   icon: <GlobalOutlined aria-label="" />,
        //   hidden: !isRS(),
        // },
        {
          key: 'users-organizations-search',
          label: t('search-users-or-organizations'),
          icon: <SearchOutlined aria-label="" />,
          onClick: () => {
            setOpenUsersOrOrganizationsSearchModal(true);
          },
          hidden: isRS(),
        },
        // {
        //   label: <LinkText text={t('note.note')} color="normal" href="/note" />,
        //   key: 'note',
        //   icon: <ReadOutlined />,
        //   hidden: isRS(),
        // },
        // {
        //   label: (
        //     <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        //       <span>{t('language')}</span>
        //       <LanguageSwitcher size="small" />
        //     </div>
        //   ),
        //   key: 'language-switcher',
        //   icon: <GlobalOutlined aria-label="" />,
        //   // No onClick needed as LanguageSwitcher handles its own logic
        // },
        {
          label: t('logout'),
          key: 'logout',
          icon: <LogoutOutlined aria-label="" />,
          onClick: () => {
            setOpenLogoutModal(true);
          },
        },
      ].filter((item) => !item.hidden),
    [t],
  );

  return (
    <>
      <SidebarContent
        collapsed={collapsed}
        onCollapse={(value) => {
          setCollapsed(value);
        }}
        accountName={me.data?.name ?? ''}
        organizationName={me.data?.organizations
          .map((d) => d.name)
          .join(ITEM_SPLITTER)}
        iconUrl={me.data?.iconDataUrl}
        selectedKey={selectedKey}
        logo={<ServiceLogo collapsed={collapsed} />}
        topMenu={topMenu}
        bottomMenu={bottomMenu}
        secondLastLoginTime={me.data?.secondLastLoginTime}
      />
      <LogoutModal
        open={openLogoutModal}
        onClose={() => {
          setOpenLogoutModal(false);
        }}
      />
      <SearchUsersModal
        open={openUsersOrOrganizationsSearchModal}
        onClose={() => {
          setOpenUsersOrOrganizationsSearchModal(false);
        }}
      />
    </>
  );
};
